import { useEffect, useRef } from 'react';
import { useFollowedVehicleStore } from '../store/useFollowedVehicleStore';
import { useGcsStore } from '../store/useGcsStore';
import { selectVehicleStatusCategory } from '../store/selectors/selectVehicleStatusCategory';
import { VehicleStatusCategory } from '../types/ui/vehicleStatusCategory';

const AUTO_FOLLOW_FEATURE_ENABLED = import.meta.env.VITE_ENABLE_AUTO_FOLLOW_FEATURE === 'true';

export function useAutoFollowDroneSound() {
  const isAutoFollowEnabled = useFollowedVehicleStore((state) => state.isAutoFollowEnabled);
  const followedVehicle = useFollowedVehicleStore((state) => state.followedVehicle);

  const statusCategory = useGcsStore((state) => {
    if (!followedVehicle) return null;
    return selectVehicleStatusCategory(state.data, followedVehicle.gcsId, followedVehicle.vehicleId);
  });

  const isInflight =
    statusCategory === VehicleStatusCategory.INFLIGHT_NOMINAL ||
    statusCategory === VehicleStatusCategory.INFLIGHT_FAILURE_MODE;

  const shouldPlay = AUTO_FOLLOW_FEATURE_ENABLED && isAutoFollowEnabled && isInflight;

  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    if (!shouldPlay) return;

    let cancelled = false;
    const ctx = new AudioContext();
    audioCtxRef.current = ctx;

    fetch('/sounds/drone-flight.mp3')
      .then((res) => res.arrayBuffer())
      .then((arrayBuffer) => ctx.decodeAudioData(arrayBuffer))
      .then((audioBuffer) => {
        if (cancelled) return;
        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        source.loop = true;
        source.connect(ctx.destination);
        source.start();
        sourceNodeRef.current = source;
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      sourceNodeRef.current?.stop();
      sourceNodeRef.current = null;
      ctx.close();
      audioCtxRef.current = null;
    };
  }, [shouldPlay]);
}
